<template>
  <div>
    <Head title="Verifikasi Laporan Penggunaan Anggaran Beasiswa" />
    <h1 class="mb-8 text-xl font-bold">
      <Link class="text-indigo-400 hover:text-indigo-600" href="/verify-accountability-reports">
        Verifikasi Laporan Penggunaan Anggaran Beasiswa {{ jenisBeasiswa }}
      </Link>
      <span class="font-medium text-indigo-400">/</span>
      {{ application.nama }}
    </h1>
    <div class="w-full mb-8 bg-white rounded-md shadow">
      <div class="flex justify-between w-full px-8 py-6 bg-gray-200">
        <h2 class="text-xl">Informasi Laporan Penggunaan Anggaran Beasiswa</h2>
        <div class="flex">
          <Button
            v-if="canWrite('verify permohonan beasiswa') && !isPenolakanAktif && isAllFileVerified"
            type="button" class="mr-2 btn-green" @click.prevent="verifyApplication"
          >
            <span><font-awesome-icon icon="fas fa-check" class="mr-2" />Terima</span>
          </Button>
          <Button
            v-if="canWrite('verify permohonan beasiswa') " type="button"
            class="mr-2 btn-warning" @click.prevent="cancelApplication"
          >
            <span><font-awesome-icon icon="fas fa-ban" class="mr-2" />Tolak</span>
          </Button>
        </div>
      </div>
      <div class="flex w-full">
        <dl class="flex flex-wrap w-full p-6 leading-10 lg:w-1/2">
          <dt class="w-full lg:w-3/12">NIM Pemohon</dt>
          <dd class="w-full lg:w-9/12">: {{ application.nim }}</dd>
          <dt class="w-full lg:w-3/12">Nama Pemohon</dt>
          <dd class="w-full lg:w-9/12">: {{ application.nama }}</dd>
          <dt class="w-full lg:w-3/12">Nama Universitas</dt>
          <dd class="w-full lg:w-9/12">: {{ application.nama_universitas }}</dd>
          <dt class="w-full lg:w-3/12">Program Studi</dt>
          <dd class="w-full lg:w-9/12">: {{ application.nama_prodi }}</dd>
          <dt class="w-full lg:w-3/12">Jenis Beasiswa</dt>
          <dd class="w-full lg:w-9/12">: {{ jenisBeasiswa }} {{ application.jenis_pt }} {{ application.jenis }}</dd>
        </dl>
        <div class="flex flex-wrap w-full p-6 leading-10 lg:w-1/2">
          <dt class="w-full lg:w-4/12">Semester</dt>
          <dd class="w-full lg:w-8/12">: {{ application.semester }}</dd>
          <dt class="w-full lg:w-4/12">Nilai IPK</dt>
          <dd class="w-full lg:w-8/12">: {{ application.ipk }}</dd>
          <dt class="w-full lg:w-4/12">No Rekening</dt>
          <dd class="w-full lg:w-8/12">: {{ application.account_number }}</dd>
          <dt class="w-full lg:w-4/12">Nominal UKT</dt>
          <dd class="w-full lg:w-8/12">:  {{ formatCurrency(application.nilai_ukt) }}</dd>
          <dt class="w-full lg:w-4/12">Nominal Beasiswa</dt>
          <dd class="w-full lg:w-8/12">:  {{ formatCurrency(nominalBeasiswa) }}</dd>
          <dt class="w-full lg:w-4/12">Total Anggaran</dt>
          <dd class="w-full lg:w-8/12">: {{ formatCurrency(totalAnggaran) }}</dd>
        </div>
      </div>
    </div>
    <!-- Informasi Perubahan UKT -->
    <div v-if="application.ukt_nominal" class="w-full my-8 bg-white rounded-md shadow">
      <div class="flex justify-between w-full px-8 py-6 bg-yellow-100">
        <h2 class="text-xl font-semibold text-yellow-800">Informasi Perubahan UKT</h2>
      </div>
      <div class="flex flex-wrap w-full p-6 leading-10">
        <dt class="w-full font-medium lg:w-3/12">Nominal UKT Baru</dt>
        <dd class="w-full lg:w-9/12">: {{ formatCurrency(application.ukt_nominal) }}</dd>
        <dt class="w-full font-medium lg:w-3/12">Catatan</dt>
        <dd class="w-full lg:w-9/12">: {{ application.ukt_catatan ?? '-' }}</dd>
        <dt class="w-full font-medium lg:w-3/12">Bukti Perubahan UKT</dt>
        <dd class="w-full lg:w-9/12">
          <span>: </span>
          <Button
            v-if="application.ukt_bukti_path"
            type="button"
            class="btn-green-sm"
            @click.prevent="viewImage(application.ukt_bukti_path)"
          >
            <font-awesome-icon icon="fas fa-magnifying-glass" class="mr-1" /> Lihat Bukti
          </Button>
          <span v-else>-</span>
        </dd>
      </div>
    </div>

    <div class="w-full my-8 bg-white rounded-md shadow">
      <div class="flex justify-between w-full px-8 py-6 bg-gray-200">
        <h2 class="text-xl">Rincian Laporan Penggunaan Anggaran</h2>
      </div>

      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="font-bold text-left">
            <th class="px-6 pt-6 pb-4">Jenis </th>
            <th class="px-6 pt-6 pb-4">Uraian</th>
            <th class="px-6 pt-6 pb-4 text-right">Nominal RAB</th>
            <th class="px-6 pt-6 pb-4 text-right">Nominal Verifikasi</th>
            <th class="px-6 pt-6 pb-4 text-right">Status</th>
            <th v-if="canWrite('verify permohonan beasiswa')" class="px-6 pt-6 pb-4">Aksi</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="detail in application.details" :key="detail.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
            <td class="p-2 pl-6 border-t">
              <p class="font-semibold">{{ detail.category }}</p>
            </td>
            <td class="p-2 border-t">
              {{ detail.description }}
            </td>
            <td class="p-2 text-right border-t">
              {{ formatCurrency(detail.nominal_rab) }}
            </td>
            <td class="p-2 text-right border-t">
              {{ formatCurrency(detail.nominal_verifikasi) }}
            </td>
            <td class="p-4 text-center border-t">
              <div>
                <span v-if="detail.status == 4" class="px-4 py-1 text-sm bg-yellow-100 rounded-lg">Draft</span>
                <span v-else-if="detail.status == 5" class="px-4 py-2 text-sm bg-yellow-500 rounded-lg">Belum Verifikasi</span>
                <span v-else-if="detail.status == 6" class="px-4 py-2 text-sm bg-red-500 rounded-lg">Tidak Memenuhi</span>
                <span v-else class="px-4 py-2 text-sm bg-green-500 rounded-lg">Disetujui</span>
              </div>
              <p class="mt-6 leading-4">{{ detail.catatan_verifikasi }}</p>
            </td>
            <td v-if="canWrite('verify permohonan beasiswa')" class="w-px p-4 border-t ">
              <Button
                v-if="detail.status == 5" type="button" class="rounded-l rounded-r btn-green-sm" title="Update Nominal Verifikasi"
                @click.prevent="updateNominalVerify(detail)"
              >
                <font-awesome-icon icon="fas fa-check" />
              </Button>
              <Button v-if="detail.status != 6" class="btn-red-sm" title="Tolak" @click.prevent="tolakDetailAnggaran(detail.id)">
                <font-awesome-icon icon="fas fa-xmark" />
              </Button>
              <Button
                type="button" title="Lihat File" :class="{ 'rounded-l': detail.status !== 1 }"
                class="rounded-r btn-green-sm" @click.prevent="viewImage(detail.file_path)"
              >
                <font-awesome-icon icon="fas fa-magnifying-glass" />
              </Button>
            </td>
          </tr>
          <tr v-if="application.details.length === 0">
            <td class="px-6 py-4 border-t" :colspan="can('write permohonan beasiswa')? 5 : 4">Tidak ada data penggunaan anggaran yang diupload.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="w-full bg-white rounded-md shadow">
      <div class="flex justify-between w-full px-8 py-6 bg-gray-200">
        <h2 class="text-xl">File Persyaratan</h2>
      </div>
      <table v-if="!statusInput" class="w-full whitespace-nowrap">
        <thead>
          <tr class="font-bold text-left">
            <th class="px-6 pt-6 pb-4">Jenis File</th>
            <th class="px-6 pt-6 pb-4 text-center">Status Verifikasi</th>
            <th v-if="canWrite('verify permohonan beasiswa')" class="px-6 pt-6 pb-4 border-b">Aksi</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="file in application.files" :key="file.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
            <td class="p-4 border-t">
              {{ file.persyaratan }}
            </td>
            <td class="p-4 text-center border-t">
              <div>
                <span v-if="file.status == 5" class="px-4 py-2 text-sm bg-yellow-500 rounded-lg">Belum Verifikasi</span>
                <span v-else-if="file.status == 6" class="px-4 py-2 text-sm bg-red-500 rounded-lg">Tidak Memenuhi</span>
                <span v-else class="px-4 py-2 text-sm bg-green-500 rounded-lg">Disetujui</span>
              </div>
              <p class="mt-6 leading-4">{{ file.catatan_verifikasi }}</p>
            </td>
            <td v-if="canWrite('verify permohonan beasiswa')" class="w-px p-4 border-t">
              <Button
                v-if="file.status == 5" class="rounded-l btn-green-sm" title="Setujui"
                @click.prevent="setujuiFile(file.id)"
              >
                <font-awesome-icon icon="fas fa-check" />
              </Button>
              <Button v-if="file.status != 6" class="btn-red-sm" title="Tolak" @click.prevent="tolakFile(file.id)">
                <font-awesome-icon icon="fas fa-xmark" />
              </Button>
              <Button
                type="button" title="Lihat File" :class="{ 'rounded-l': file.status !== 1 }"
                class="rounded-r btn-green-sm" @click.prevent="viewImage(file.file_path)"
              >
                <font-awesome-icon icon="fas fa-magnifying-glass" />
              </Button>
            </td>
          </tr>
          <tr v-if="application.files.length === 0">
            <td class="px-6 py-4 border-t" colspan="3">Tidak ada data file persayaratan yang diupload.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <image-viewer :src="imageSource" :show-modal="showImage" @close="showImage = false" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import ImageViewer from '../../Shared/ImageViewer.vue'

export default {
  components: {
    Head,
    Link,
    ImageViewer,
  },
  layout: Layout,
  props: {
    application: Object,
    jenisBeasiswa: String,
    nominalBeasiswa: Number,
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        scholar_application_id: this.application.id,
        type: null,
        file: null,
      }),
      imageData: '',
      statusInput: false,
      imageSource: '',
      showImage: false,
      catatan_verifikasi: '',
    }
  },
  computed: {

    isAllFileVerified() {
      var validity = true

      for (let index = 0; index < this.application.details.length; index++) {
        const file = this.application.details[index]

        if (file.status == 5) {
          validity = false
          break
        }
      }

      for (let index = 0; index < this.application.files.length; index++) {
        const file = this.application.files[index]

        if (file.status == 5) {
          validity = false
          break
        }
      }
      return validity
    },
    isPenolakanAktif() {
      var validity = false


      for (let index = 0 ; index < this.application.details.length ; index++) {
        const file = this.application.details[index]
        if (file.status != 7) {
          validity = true
          break
        }
      }

      for (let index = 0 ; index < this.application.files.length ; index++) {
        const file = this.application.files[index]
        if (file.status != 7) {
          validity = true
          break
        }
      }

      return validity
    },

    totalAnggaran() {
      return this.application.details.reduce((sum, detail) => sum + (Number(detail.nominal_rab) || 0), 0)
    },
  },

  methods: {
    viewImage(imgSource) {
      this.imageSource = imgSource
      this.showImage = true
    },
    async updateNominalVerify(detail) {
      const nominalAwal = detail.nominal_verifikasi ?? detail.nominal_spj

      const { value: nominalFormatted } = await this.$swal.fire({
        title: 'Update Nominal Verifikasi',
        html: `
      <label for="nominal" class="swal2-label">Masukkan nominal yang disetujui</label>
      <input id="nominal" type="text" class="swal2-input" value="${this.formatCurrency(nominalAwal)}" autocomplete="off">
    `,
        showCancelButton: true,
        confirmButtonText: 'Update',
        cancelButtonText: 'Batal',
        focusConfirm: false,
        preConfirm: () => {
          const input = document.getElementById('nominal').value
          const angka = Number(input.replace(/[^0-9]/g, ''))
          if (!angka || angka < 0) {
            this.$swal.showValidationMessage('Nominal tidak boleh kosong atau negatif!')
            return false
          }
          return angka
        },
        didOpen: () => {
          const input = document.getElementById('nominal')
          input.addEventListener('input', e => {
            const angka = Number(e.target.value.replace(/[^0-9]/g, ''))
            e.target.value = this.formatCurrency(angka)
          })
        },
      })

      if (nominalFormatted) {
        this.$inertia.put(
          `/accountability-report-details/${detail.id}/verify-nominal`,
          { nominal_verifikasi: nominalFormatted },
          { preserveScroll: true },
        )
      }
    },

    async tolakDetailAnggaran(id) {
      const { value: catatan } = await this.$swal.fire({
        title: 'Anda yakin tolak file ini?',
        input: 'text',
        inputLabel: 'Masukan Alasan Penolakan',
        inputValue: '',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya',
        cancelButtonText: 'Tidak',
        inputValidator: (value) => {
          if (!value) {
            return 'Catatan verifikasi harus diisi!'
          }
        },
      })

      if (catatan) {
        this.$inertia.put(`/accountability-report-details/${id}/reject`, {
          'catatan_verifikasi': catatan,
        }, {
          preserveScroll: true,
        })
      }
    },
    setujuiFile(id) {
      this.$swal.fire({
        title: 'Anda yakin setujui file ini?',
        text: 'Harap verifikasi ulang sebelum merubah status!',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya',
        cancelButtonText: 'Tidak',
      }).then((result) => {
        if (result.isConfirmed) {
          this.$inertia.put(`/accountability-report-files/${id}/accept`, {}, {
            preserveScroll: true,
          })
        }
      })
    },
    async tolakFile(id) {
      const { value: catatan } = await this.$swal.fire({
        title: 'Anda yakin tolak file ini?',
        input: 'text',
        inputLabel: 'Masukan Alasan Penolakan',
        inputValue: '',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya',
        cancelButtonText: 'Tidak',
        inputValidator: (value) => {
          if (!value) {
            return 'Catatan verifikasi harus diisi!'
          }
        },
      })

      if (catatan) {
        this.$inertia.put(`/accountability-report-files/${id}/cancel`, {
          'catatan_verifikasi': catatan,
        }, {
          preserveScroll: true,
        })
      }
    },
    formatCurrency(value) {
      if (value === null || value === undefined) {
        return 'Rp 0'
      }
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value)
    },
    verifyApplication() {
      this.$swal.fire({
        title: 'Anda yakin setujui permohonan anggaran beasiswa ini?',
        text: 'Harap verifikasi ulang sebelum merubah status!',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya',
        cancelButtonText: 'Tidak',
      }).then((result) => {
        if (result.isConfirmed) {
          this.$inertia.put(`/accountability-reports/${this.application.id}/accept`)
        }
      })
    },
    async cancelApplication() {
      const { value: catatan } = await this.$swal.fire({
        title: 'Anda yakin batalkan pelaporan anggaran beasiswa ini?',
        input: 'text',
        inputLabel: 'Masukan Alasan Pembatalan',
        inputValue: '',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya',
        cancelButtonText: 'Tidak',
        inputValidator: (value) => {
          if (!value) {
            return 'Catatan verifikasi harus diisi!'
          }
        },
      })

      if (catatan) {
        this.$inertia.put(`/accountability-reports/${this.application.id}/revisi`, {
          'catatan_verifikasi': catatan,
        })
      }
    },

  },
}
</script>
